import { endpoints } from '@/services/endpoints'
import { createSharedSseConnection } from '@/services/sse'

/**
 * The account-wide anomaly list stream, as one shared connection.
 *
 * ── Why a module singleton rather than a hook's own socket ───────────────
 * On `/app/anomalies` the list page and the app shell's severity band are
 * mounted at the same time and both want this stream. A per-hook connection
 * would open two, and spare connections are not free: on HTTP/1.1 the
 * six-per-origin cap is close enough that reaching it makes every ordinary
 * request block indefinitely — which presents as the app freezing rather than as
 * an error. Ref-counting keeps it at one however many subscribers there are.
 *
 * The five-second linger is for navigation: list → detail → back would otherwise
 * tear down and rebuild a Postgres `LISTEN` twice in as many seconds.
 */

/**
 * How long after the last event the cache is actually invalidated.
 *
 * A trailing-edge debounce, because detections arrive in bursts — a line
 * tripping produces a dozen rows in a second, and twelve refetches of the same
 * page is eleven wasted.
 */
export const LIST_COALESCE_MS = 400

/**
 * The ceiling on that debounce, and it is the part that is easy to leave out.
 *
 * A plain trailing debounce under a SUSTAINED stream of events never fires at
 * all — each event pushes the timer out and the screen goes stale for as long as
 * the plant is busy, which is exactly when it must not. The max-wait forces a
 * flush every two seconds regardless.
 *
 * Two seconds deliberately: that is what the poll this replaces cost, so the
 * worst case here is the old behaviour, and the ordinary case — a quiet plant —
 * is zero requests instead of one every two seconds.
 */
export const LIST_COALESCE_MAX_MS = 2000

/**
 * What a subscriber is told to do, and it is always the same thing: refetch.
 *
 * ── The event payload is a TRIGGER, not data to apply ────────────────────
 * The server sends the one row that changed. Applying it would mean patching a
 * cache that holds many entries keyed by filter, sort and page — each carrying
 * `total` and `counts` in the same response body. A changed row can enter or
 * leave a filter, move to another page, and shift two counts at once, and none
 * of that is computable here. The result would be a footer disagreeing with the
 * rows above it.
 *
 * There is a second reason, and it is the one that settles it: an event can
 * legitimately arrive with NO row at all. `last_payload()` pops server-side, so
 * with two tabs open on one account the first reader takes the payload and the
 * second is handed `{}`. A client that treated an empty event as "nothing
 * happened" would leave that second tab stale indefinitely.
 */
const listeners = new Set()
/* Declared above the connection below, which closes over them. Nothing calls
   `onStatus` during construction, so the order is not load-bearing today — it
   is here so it cannot become so. */
let statusValue = 'idle'
const statusWatchers = new Set()
let flushTimer = null
let maxWaitTimer = null
let pendingWhileHidden = false

const flush = () => {
  clearTimeout(flushTimer)
  clearTimeout(maxWaitTimer)
  flushTimer = null
  maxWaitTimer = null
  pendingWhileHidden = false
  for (const listener of listeners) listener()
}

const schedule = () => {
  /* A hidden tab still holds its socket open — an idle SSE connection costs
     nothing, while closing on every alt-tab costs a reconnect and a catch-up
     read. What it does not do is refetch: nobody is looking, and the flag below
     makes sure the return is a single refetch rather than one per event. */
  if (typeof document !== 'undefined' && document.hidden) {
    pendingWhileHidden = true
    return
  }
  clearTimeout(flushTimer)
  flushTimer = setTimeout(flush, LIST_COALESCE_MS)
  maxWaitTimer ??= setTimeout(flush, LIST_COALESCE_MAX_MS)
}

const onVisible = () => {
  if (typeof document !== 'undefined' && document.hidden) return
  if (pendingWhileHidden) flush()
}

if (typeof document !== 'undefined') {
  /* Explicitly, rather than leaning on TanStack's `refetchOnWindowFocus`: a
     visible but unfocused window gets no focus event, and a second monitor is
     the ordinary case on a plant desk. */
  document.addEventListener('visibilitychange', onVisible)
}

const connection = createSharedSseConnection({
  path: endpoints.anomalies.stream,
  lingerMs: 5000,
  onEvent: schedule,
  /* A reconnect is itself news. There is no `Last-Event-ID` replay here, so
     anything could have changed while the stream was down and the gap has to be
     treated as a change rather than as continuity. */
  onOpen: ({ reconnected }) => {
    if (reconnected) schedule()
  },
  onStatus: (status) => {
    statusValue = status
    for (const watcher of statusWatchers) watcher(status)
  },
})

/**
 * Subscribe to "the anomaly list may have changed".
 *
 * Returns an unsubscribe. The socket opens on the first subscriber and closes a
 * linger after the last.
 */
export function subscribeToAnomalyList(onChange, onStatusChange) {
  listeners.add(onChange)
  if (onStatusChange) {
    statusWatchers.add(onStatusChange)
    onStatusChange(statusValue)
  }
  const release = connection.subscribe()

  return () => {
    listeners.delete(onChange)
    if (onStatusChange) statusWatchers.delete(onStatusChange)
    release()
    if (listeners.size === 0) {
      clearTimeout(flushTimer)
      clearTimeout(maxWaitTimer)
      flushTimer = null
      maxWaitTimer = null
      pendingWhileHidden = false
    }
  }
}

export const anomalyListStreamStatus = () => statusValue
